// Blog — SEO traffic acquisition posts. Shared content model (see docs.js).

export const BLOG = [
  {
    slug: "how-to-track-link-clicks",
    category: "Analytics",
    tone: "candy",
    eyebrow: "BLOG · ANALYTICS",
    title: "How to track link clicks (the right way)",
    excerpt:
      "You can't improve what you can't measure. Here's how to track who clicks your links, from where, and on what — without a heavyweight analytics setup.",
    readTime: "6 min",
    date: "2026-05-18",
    updated: "2026-06-03",
    seo: {
      title: "How to Track Link Clicks: A Practical Guide",
      description:
        "Learn how to track link clicks — total vs unique, device and country breakdowns, referrers, and bot filtering — using short links and real-time analytics.",
      keywords: [
        "how to track link clicks",
        "link click tracking",
        "track url clicks",
        "link analytics",
      ],
    },
    sections: [
      {
        type: "intro",
        body: "Tracking link clicks used to mean wiring up an analytics platform and waiting a day for data. With a short-link tool, it's instant: every click is recorded the moment it happens, with enough context to be useful and bots filtered out so the numbers stay honest.",
      },
      { type: "h2", title: "What's worth tracking" },
      {
        type: "list",
        items: [
          "Total vs unique clicks — total shows volume, unique shows reach.",
          "Referrer — which site or app sent the click.",
          "Device, OS, and browser — how your audience actually browses.",
          "Country — where your audience is, for geo decisions.",
          "Time of day / weekday — when to post, shown as a heatmap.",
        ],
      },
      { type: "h2", title: "How to do it with LinkLane" },
      {
        type: "ol",
        items: [
          "Shorten any URL (free) — you get a short link and a QR code.",
          "Share the short link or QR anywhere.",
          "Open analytics for a live click feed, totals, uniques, and breakdowns.",
          "Export to CSV when you need the raw data.",
        ],
      },
      {
        type: "callout",
        tone: "candy",
        title: "Bots don't count",
        body: "LinkLane flags bot traffic so your unique-click and engagement numbers reflect real people, not crawlers.",
      },
      {
        type: "faq",
        title: "Click tracking FAQ",
        items: [
          {
            q: "How do I track clicks on a link?",
            a: "Shorten the URL with a link tool like LinkLane and share the short link; each click is recorded with referrer, device, country, and time, viewable in real-time analytics.",
          },
          {
            q: "What's the difference between total and unique clicks?",
            a: "Total counts every click; unique counts distinct visitors (deduplicated), so you can tell volume apart from reach.",
          },
        ],
      },
      {
        type: "cta",
        eyebrow: "MEASURE IT",
        title: "See every click.",
        to: "/signup",
        label: "Start free",
      },
    ],
    faq: [
      {
        q: "Can I track who clicks my link?",
        a: "You can track aggregate context — referrer, device, OS, browser, country, and time — per click, with bots filtered. LinkLane shows this in a real-time feed and breakdowns.",
      },
    ],
    related: ["guides:utm-tracking", "guides:qr-codes-for-marketing", "compare:linklane-vs-bitly"],
  },

  {
    slug: "what-is-a-branded-link",
    category: "Link management",
    tone: "mint",
    eyebrow: "BLOG · LINK MANAGEMENT",
    title: "What is a branded link (and why it matters)",
    excerpt:
      "Branded links replace a generic short domain with your own — and they get clicked more. Here's what they are, how they work, and when to use them.",
    readTime: "5 min",
    date: "2026-05-06",
    updated: "2026-06-03",
    seo: {
      title: "What Is a Branded Link? Definition & Examples",
      description:
        "A branded link uses your own domain instead of a generic shortener. Learn what branded links are, why they boost trust and clicks, and how to set them up.",
      keywords: [
        "branded link",
        "what is a branded link",
        "custom domain short link",
        "branded short url",
      ],
    },
    sections: [
      {
        type: "intro",
        body: "A branded link is a short link that uses your own domain — yourbrand.link/launch instead of a generic shortener. It's a small change with a real effect: people trust and click links that carry a name they recognize.",
      },
      { type: "h2", title: "Why branded links matter" },
      {
        type: "list",
        items: [
          "Trust — a recognizable domain signals the link is really from you.",
          "Click-through — branded links consistently outperform generic ones.",
          "Recall — your domain reinforces your brand every time the link is shared.",
          "Control — you own the domain, so you own the link's destiny.",
        ],
      },
      { type: "h2", title: "How to use them in LinkLane" },
      {
        type: "p",
        body: "On the Enterprise plan you can add a custom domain with DNS verification and serve your short links from it. Every link, QR code, and campaign then carries your brand instead of a generic host — while keeping the analytics, routing, and protection features intact.",
      },
      {
        type: "faq",
        title: "Branded links FAQ",
        items: [
          {
            q: "What is a branded link?",
            a: "A short link on your own custom domain (e.g. yourbrand.link/sale) instead of a generic shortener, which increases trust and click-through.",
          },
          {
            q: "How do I set up a branded link?",
            a: "On LinkLane Enterprise, add your domain and verify it via DNS; your short links then resolve from your branded host.",
          },
        ],
      },
      {
        type: "cta",
        eyebrow: "BRAND IT",
        title: "Links that carry your name.",
        to: "/pricing",
        label: "See plans",
      },
    ],
    faq: [
      {
        q: "Do branded links get more clicks?",
        a: "Yes — links on a recognizable, owned domain consistently see higher trust and click-through than generic short links.",
      },
    ],
    related: ["compare:linklane-vs-rebrandly", "use-cases:agencies", "guides:utm-tracking"],
  },
];

export const BLOG_BY_SLUG = Object.fromEntries(BLOG.map((e) => [e.slug, e]));
